import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Percent, Save, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mis empresas → ¿esta empresa es AGENTE DE RETENCIÓN de IVA en sus compras?
 * Si lo es, al pagar a proveedores se descuenta la retención (ver Configuración global →
 * Retenciones para la tabla de % y el sueldo mínimo). Guarda por su cuenta (no usa el
 * botón Actualizar de la ficha).
 */
export default function RetencionAgentePanel({ token, slug, canEdit }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    if (!slug) return;
    fetch(`${API}/admin/retenciones/agente?logo_tipo=${encodeURIComponent(slug)}`, { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(setCfg)
      .catch(() => setCfg(null));
  }, [slug, token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!slug || !cfg) return null;

  const guardar = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/retenciones/agente`, {
        method: "PUT", headers,
        body: JSON.stringify({ ...cfg, logo_tipo: slug }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo guardar");
      setCfg(data);
      toast.success("Configuración de retención guardada");
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const inp = "bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60 disabled:opacity-50";

  return (
    <div className="border border-white/10 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2 text-slate-200 text-sm font-medium">
        <Percent className="w-4 h-4 text-amber-400" /> Retención de IVA en compras
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" disabled={!canEdit} checked={!!cfg.es_agente}
          onChange={e => setCfg(c => ({ ...c, es_agente: e.target.checked }))} />
        Esta empresa es agente de retención (retiene IVA a sus proveedores al pagar)
      </label>
      {cfg.es_agente && (
        <div className="grid sm:grid-cols-2 gap-3 pl-6">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Cómo se define el porcentaje</label>
            <select className={`${inp} w-full`} disabled={!canEdit} value={cfg.modo}
              onChange={e => setCfg(c => ({ ...c, modo: e.target.value }))}>
              <option value="tabla">Según tipo de comprobante (tabla global)</option>
              <option value="fijo">Porcentaje fijo sobre el IVA</option>
            </select>
          </div>
          {cfg.modo === "fijo" && (
            <div>
              <label className="text-xs text-slate-400 block mb-1">% del IVA a retener</label>
              <input type="number" min="0" max="100" className={`${inp} w-full`} disabled={!canEdit}
                value={cfg.pct_fijo} onChange={e => setCfg(c => ({ ...c, pct_fijo: e.target.value }))} />
            </div>
          )}
          <p className="sm:col-span-2 text-[11px] text-slate-500">
            Aplica el umbral de 10 jornales mínimos por proveedor y mes, y respeta las constancias de exportador / no retención cargadas en el Catastro.
            Rige solo para pagos nuevos; los anteriores no se tocan.
          </p>
        </div>
      )}
      {canEdit ? (
        <button type="button" onClick={guardar} disabled={saving}
          className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-xs flex items-center gap-2">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar retención
        </button>
      ) : (
        <p className="text-[11px] text-slate-500">Solo lectura: se necesita el permiso retenciones.editar_config.</p>
      )}
    </div>
  );
}
